import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sparkles, Send, BookOpen } from "lucide-react";
import { toast } from "sonner";

const SAMPLES = [
  "What should a CFO own in the first 90 days?",
  "How do reserved matters differ from management delegations?",
  "What's a worker's remedy for retaliation?",
  "Explain decision rights for a CTO in a Series A company.",
  "How to run a weekly cadence as a COO?",
  "What is good governance in a family-owned enterprise?",
];

const PILLARS = [
  { v: "", l: "All pillars" },
  { v: "discover", l: "Discover" },
  { v: "learn", l: "Learn" },
  { v: "practice", l: "Practice" },
  { v: "earn", l: "Earn" },
  { v: "advance", l: "Advance" },
  { v: "protect", l: "Protect" },
];

export default function Tribunal() {
  const [params] = useSearchParams();
  const [turns, setTurns] = useState([]);
  const [q, setQ] = useState("");
  const [pillar, setPillar] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    const initial = params.get("q");
    if (initial && turns.length === 0) {
      setQ(initial);
      setTimeout(() => ask(initial), 10);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [turns, busy]);

  const ask = async (text) => {
    const question = (text ?? q).trim();
    if (!question || busy) return;
    setBusy(true);
    setTurns((t) => [...t, { role: "user", content: question }]);
    setQ("");
    try {
      const { data } = await api.post("/tribunal/ask", {
        question, pillar: pillar || undefined,
      });
      setTurns((t) => [...t, { role: "advisor", content: data.answer, citations: data.citations, grounded: data.grounded }]);
    } catch (e) {
      toast.error("Advisor unavailable. Please retry.");
      setTurns((t) => [...t, { role: "advisor", content: "I ran into an issue. Please try again shortly.", citations: [], grounded: false }]);
    } finally { setBusy(false); }
  };

  const renderAnswer = (text, citations) => {
    // Linkify [n] citations
    const parts = text.split(/(\[\d+\])/g);
    return parts.map((p, i) => {
      const m = p.match(/^\[(\d+)\]$/);
      if (m) {
        const n = parseInt(m[1], 10);
        const cite = citations?.find((c) => c.n === n);
        if (cite) {
          return (
            <Link key={i} to={`/app/learn/chapter/${cite.chapter_id}`}
              className="inline-flex align-super text-[10px] font-mono text-primary hover:underline mx-0.5"
              title={cite.title}>
              [{n}]
            </Link>
          );
        }
      }
      return <span key={i}>{p}</span>;
    });
  };

  return (
    <div className="grid lg:grid-cols-12 gap-8 h-[calc(100vh-10rem)]" data-testid="tribunal-page">
      <div className="lg:col-span-9 flex flex-col h-full">
        <div className="mb-4">
          <div className="label-caps mb-2 flex items-center gap-1.5"><Sparkles className="h-3 w-3 text-primary" /> Tribunal Advisor</div>
          <h1 className="text-2xl md:text-3xl tracking-tight font-semibold">Grounded answers, with citations.</h1>
        </div>

        <Card className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-5 md:p-6 space-y-6" data-testid="tribunal-stream">
            {turns.length === 0 && (
              <div className="h-full flex flex-col justify-center items-center text-center py-10">
                <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-5">
                  <Sparkles className="h-6 w-6 text-primary" />
                </div>
                <div className="text-lg font-medium">Ask the tribunal.</div>
                <div className="text-sm text-muted-foreground mt-2 max-w-md">
                  Grounded in 271 volumes of executive practice. Answers include inline citations you can tap to read the source.
                </div>
                <div className="mt-6 flex flex-wrap justify-center gap-2 max-w-xl">
                  {SAMPLES.map((s) => (
                    <button key={s} onClick={() => ask(s)} data-testid={`sample-${s.length}`}
                      className="px-3 py-1.5 rounded-full border border-border text-xs text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors duration-200">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {turns.map((t, i) => (
              <div key={i} className={t.role === "user" ? "flex justify-end" : ""}>
                {t.role === "user" ? (
                  <div className="max-w-[80%] px-4 py-2.5 rounded-2xl bg-primary text-primary-foreground text-sm">
                    {t.content}
                  </div>
                ) : (
                  <div className="max-w-full">
                    <div className="text-sm leading-relaxed whitespace-pre-wrap">
                      {renderAnswer(t.content, t.citations)}
                    </div>
                    {t.citations?.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-border">
                        <div className="label-caps mb-2">Citations</div>
                        <div className="space-y-1">
                          {t.citations.map((c) => (
                            <Link key={c.n} to={`/app/learn/chapter/${c.chapter_id}`}
                              className="block text-xs font-mono text-muted-foreground hover:text-foreground">
                              [{c.n}] {c.title} <span className="opacity-60">· {c.topic}</span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            {busy && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="h-1.5 w-1.5 bg-primary rounded-full animate-pulse" />
                Searching the tribunal library and composing a grounded answer…
              </div>
            )}
            <div ref={endRef} />
          </div>

          <div className="p-3 border-t border-border flex gap-2 items-center">
            <Select value={pillar} onValueChange={setPillar}>
              <SelectTrigger className="h-10 w-32 shrink-0" data-testid="tribunal-pillar">
                <SelectValue placeholder="Pillar" />
              </SelectTrigger>
              <SelectContent>
                {PILLARS.map((p) => <SelectItem key={p.v || "all"} value={p.v || " "}>{p.l}</SelectItem>)}
              </SelectContent>
            </Select>
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), ask())}
              placeholder="Ask the tribunal anything about vocation, leadership, rights…"
              disabled={busy}
              data-testid="tribunal-input"
              className="flex-1 h-10 bg-transparent border border-input rounded-full px-4 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
            <Button onClick={() => ask()} disabled={busy || !q.trim()} data-testid="tribunal-send" className="rounded-full h-10 px-5">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </Card>
      </div>

      <aside className="hidden lg:block lg:col-span-3 space-y-5">
        <Card className="p-5">
          <BookOpen className="h-4 w-4 text-primary mb-3" />
          <div className="text-sm font-medium">How grounding works</div>
          <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
            Your question is matched against 100k+ indexed chunks of expert content. The top passages are passed
            to Claude Sonnet 4.5 which must answer only from those sources, citing them inline.
          </p>
        </Card>
        <Card className="p-5">
          <div className="label-caps mb-2">Scope</div>
          <ul className="text-xs text-muted-foreground space-y-1">
            <li>· Executive roles & cadences</li>
            <li>· Governance & decision rights</li>
            <li>· Judgment under uncertainty</li>
            <li>· Succession & careers</li>
            <li>· Compensation structures</li>
            <li>· Worker rights & protections</li>
          </ul>
        </Card>
      </aside>
    </div>
  );
}
